
import React from "react";
import type { MortgageData } from "../CalculatorTabs";
import { AutoCalculateOptions } from "../utils/calculatorUtils";

// Import the smaller components
import VAStatusSelect from "./vaLoan/VAStatusSelect";
import HomebuyingHistorySelect from "./vaLoan/HomebuyingHistorySelect";
import VADisabilityStatusSelect from "./vaLoan/VADisabilityStatusSelect";
import VAFundingFeeInfo from "./vaLoan/VAFundingFeeInfo";
import FundingFeeToggle from "./subcomponents/FundingFeeToggle";
import { VAExemptionBreakdown } from "./vaLoan/VAExemptionBreakdown";
import { StateVABenefitsInfo } from "./vaLoan/StateVABenefitsInfo";

interface VALoanFieldsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
}

const VALoanFields: React.FC<VALoanFieldsProps> = ({ mortgageData, setMortgageData, autoCalculate }) => {
  // Skip funding fee toggle if exempt from funding fee
  const isExemptFromFundingFee = mortgageData.vaDisabilityStatus === "partial" || mortgageData.vaDisabilityStatus === "full";
  
  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <h3 className="font-medium text-sm">VA Loan Details</h3>
      
      {/* VA Status */}
      <VAStatusSelect 
        mortgageData={mortgageData} 
        setMortgageData={setMortgageData} 
        autoCalculate={autoCalculate} 
      />
      
      {/* Homebuying History */}
      <HomebuyingHistorySelect 
        mortgageData={mortgageData} 
        setMortgageData={setMortgageData} 
        autoCalculate={autoCalculate} 
      />
      
      {/* VA Disability Status */}
      <VADisabilityStatusSelect 
        mortgageData={mortgageData} 
        setMortgageData={setMortgageData} 
        autoCalculate={autoCalculate} 
      />
      
      {/* Funding Fee Toggle - only show if not exempt from funding fee */}
      {!isExemptFromFundingFee && mortgageData.vaStatus && mortgageData.homebuyingHistory && (
        <FundingFeeToggle
          mortgageData={mortgageData}
          setMortgageData={setMortgageData}
          autoCalculate={autoCalculate}
          label="Roll VA Funding Fee into Loan"
          tooltipContent="When selected, the VA funding fee will be added to your loan amount instead of being paid at closing. This will increase your loan amount and monthly payment, but reduce your out-of-pocket costs at closing."
        />
      )}
      
      {/* VA Exemption Breakdown */}
      {mortgageData.vaDisabilityStatus && mortgageData.vaDisabilityStatus !== 'none' && (
        <VAExemptionBreakdown mortgageData={mortgageData} />
      )}
      
      {/* State VA Benefits Information */}
      {mortgageData.vaDisabilityStatus && mortgageData.vaDisabilityStatus !== 'none' && (
        <StateVABenefitsInfo state={mortgageData.state} />
      )}

      {/* VA Funding Fee Information */}
      {mortgageData.vaStatus && mortgageData.homebuyingHistory && (
        <VAFundingFeeInfo mortgageData={mortgageData} />
      )}
    </div>
  );
};

export { VALoanFields };
